import React from 'react';
import { Route, useHistory } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { OktaAuth } from '@okta/okta-auth-js';
import { LoginCallback, Security } from '@okta/okta-react';
import { setAppLoader } from '../redux/app/actions';
import { clearRedis } from "../services/appServices";
import { clearSession, DATADOG_LOG_TYPE, datadogLogger } from './utilCommon';
import { removeAppCookies } from "./manageCookies";
import { APP_LOGOUT_PARAMS, PAGE_URL, SESSION_STORAGE_KEYS } from 'models/common.models';
import { restoreOktaSession } from './OktaSessionRestore';
import CustomLoader from 'components/atoms/CustomLoader';

export let PROTACTED_HOME_PAGE = '/MyASTM';

// Okta Scopes
export const OKTA_SCOPES = ['openid', 'profile', 'email'];

// Setup Configuration for OktaAuth. 
export const authClient = new OktaAuth({
    issuer: process.env.REACT_APP_OKTA_ISSUER,
    clientId: process.env.REACT_APP_OKTA_CLIENT_ID,
    redirectUri: process.env.REACT_APP_REDIRECT_URI,
    scopes: OKTA_SCOPES,
    pkce: true, // [true] provides a modern solution for protecting SPAs. 
    postLogoutRedirectUri: process.env.REACT_APP_LOGOUT_URI,
    disableHttpsCheck: window.location.hostname !== 'localhost' ? true : false, // [True] if working on https environment.
    tokenManager: {
        expireEarlySeconds: 60,
        autoRenew: true, // autorenew [true] auto update access token
        storage: 'memory',
        syncStorage: false,
    },
    storageManager: {
        token: {
            storageType: 'memory',
        },
        cache: {
            storageType: 'memory',
        },
        transaction: {
            storageType: 'memory',
        },
    },
    services: {
        autoRenew: true,
        autoRemove: true,
    }
});

export const handleLoggedIn = async (
  redirectURL = "",
  isSessionClear = true,
  isLogoutFromExternal = false
) => {
  let redirectCallbackURL = "";
  if (redirectURL) {
    redirectCallbackURL += `?callbackUrl=${window.btoa(redirectURL)}`;
  }
  const genratedRedirectURL = isLogoutFromExternal
    ? process.env.REACT_APP_LOGIN_PAGE_REQUEST +
      window.btoa(redirectURL + redirectCallbackURL)
    : process.env.REACT_APP_LOGIN_PAGE_REQUEST +
      window.btoa(window.location.origin + redirectCallbackURL);

  //Remove all storage and cookies before redirect to login page
  if (isSessionClear) {
    clearSession();
    removeAppCookies(false);
  }

  window.location.assign(genratedRedirectURL);
};

export const clearAppSession = async (isPageReload = false) => {
  clearSession();
  removeAppCookies(false);
  authClient.tokenManager.clear();
  if (isPageReload) {
    window.location.reload();
  }
};

export const redirectLoginHandler = async (clearSession = true, redirectURL, isLogoutFromExternal) => {
    isHandleLogoutRedirectCalled = true;
    if (clearSession) {
        clearAppSession();
    }
    await authClient.signOut();
    handleLoggedIn(redirectURL, false, isLogoutFromExternal);
}

let isHandleLogoutRedirectCalled = false;
export const handleLoggedOut = async (
  dispatch,
  redirectURL = "",
  isLogoutFromExternal = false
) => {
  if (isHandleLogoutRedirectCalled) {
    return;
  }
  dispatch(setAppLoader(true));
  clearRedis(async () => {
    window.localStorage.setItem("isMemberStillLoginInAnotherTab", false);
    redirectLoginHandler(true, redirectURL, isLogoutFromExternal);
  });
};

export const redirectUrlHandler = () => {
    const redirectUrl = sessionStorage.getItem(SESSION_STORAGE_KEYS.REDIRECT_URL);
    return redirectUrl || null;
}

export const oktaExceptionHandler = (error) => {
    datadogLogger(
        `OAuthError or AuthSdkError Error: ${error?.name}`,
        {
            error,
            config: error?.config,
        },
        DATADOG_LOG_TYPE.error,
        {
            error
        }
    );
    clearRedis(() => {
        clearSession();
        removeAppCookies(false);
        verifySessionAndGetToken();
    });
}

const oktaWithRedirectHandler = async (history) => {
    const hashValue = window.location.hash;
    const sessionToken = hashValue ? window.location.hash.split('#')[1] : false;
    try {
        const urlParams = new URLSearchParams(window.location.search);
        const redirectUrlParams = urlParams.get('callbackUrl');
        if (redirectUrlParams || hashValue) {
            const callbackHandler = (response) => {
                if (response) {
                    history.replace(PAGE_URL.MYASTM)
                }
            }
            const oktaProps = {
                oktaAuth: authClient,
                redirectUrl: redirectUrlParams ? window.atob(redirectUrlParams) : null,
                sessionToken: sessionToken,
                callback: callbackHandler,
                history: history
            }
            restoreOktaSession(oktaProps)
        } else {
            restoreOktaSession({ oktaAuth: authClient })
        }
    } catch (error) {
        oktaExceptionHandler(error);
    }
}

export const verifySessionAndGetToken = async () => {
    const isSessionExist = await authClient.session.exists();
    if (!isSessionExist) {
        const callbackUrl = redirectUrlHandler();
        handleLoggedIn(callbackUrl ? callbackUrl : window.location.pathname + window.location.search);
    } else {
        const idToken = await authClient.tokenManager.get("idToken");
        if (!idToken) {
            oktaWithRedirectHandler();
        }
    }
};

// Get and renew auth token header.
export const getAuthHeaderToken = async () => {
    try {
      const tokenInfo = await authClient.tokenManager.get("accessToken");
      // To check token is valid.
      if (tokenInfo) {
          if (authClient.tokenManager.hasExpired(tokenInfo)) {
              const renewAccessToken = await authClient.token.renew(tokenInfo);
              return renewAccessToken ? renewAccessToken.accessToken : null;
          } else {
              return tokenInfo.accessToken;
          }
      } else {
          return null;
      }
    } catch (error) {
      oktaExceptionHandler(error);
    }
};

export const OktaSecurityHOC = (props) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const hashValue = window.location.hash;

    React.useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const isRedirectFromRM = urlParams.get(APP_LOGOUT_PARAMS.RM_LOGOUT);
        const isRedirectFromExternalApp = urlParams.get(APP_LOGOUT_PARAMS.MEMBER_APP_LOGOUT);
        const redirectUrl = urlParams.get(APP_LOGOUT_PARAMS.REDIRECT_URL);
        if (isRedirectFromRM || isRedirectFromExternalApp) {
            (async () => {
                const isSessionExist = await authClient.session.exists();
                const getRedirectPath = () => {
                    if (isRedirectFromRM) return APP_LOGOUT_PARAMS.RM_LOGIN;
                    if (isRedirectFromExternalApp) return `${redirectUrl}`;
                    return "/";
                };
                const redirectPath = getRedirectPath();
                if (!isSessionExist) {
                handleLoggedOut(
                    dispatch,
                    isRedirectFromExternalApp
                    ? window.atob(redirectPath)
                    : redirectPath,
                    isRedirectFromExternalApp ? true : false
                );
                } else {
                window.location.replace(
                    window.location.origin + isRedirectFromExternalApp
                    ? window.atob(redirectPath)
                    : redirectPath
                );
                }
            })();
        }
        // https://github.com/okta/okta-auth-js/issues/1365#issuecomment-1387698694
        (async () => {
            await authClient.start();
        })();

        return () => {
            (async () => {
                await authClient.stop();
            })();
        }
    }, []);

    React.useEffect(() => {
      if (hashValue) {
        const sessionToken = window.location.hash.split("#")[1];
        if (sessionToken === "IdentityDelegationRefreshToken") {
          clearSession();
          window.location.replace(window.location.origin);
        } else {
          const urlParams = new URLSearchParams(window.location.search);
          const redirectUrlParams = urlParams.get("callbackUrl");
          if (redirectUrlParams) {
            window.sessionStorage.setItem(
              SESSION_STORAGE_KEYS.REDIRECT_CALLBACK_URL,
              window.location.pathname?.length
                ? window.atob(redirectUrlParams)
                : PAGE_URL.MYASTM,
            );
          }
          oktaWithRedirectHandler(history);
        }
      }
    }, [hashValue]);

    if (hashValue) {
        return <CustomLoader testId='app-spinner' />;
    }

    const restoreOriginalUri = async (_oktaAuth, originalUri) => {
        window.history.replaceState({}, '', originalUri || '/');
    };

    const handleAuth = () => {
      if (!hashValue) {
        verifySessionAndGetToken();
      }
    }

    return (
        <Security 
            oktaAuth={authClient}
            onAuthRequired={handleAuth}
            restoreOriginalUri={restoreOriginalUri}
        >
            <Route path="/auth" component={LoginCallback} />
            { props.children }
        </Security>
    );
}